import {
  Button,
  Card,
  Container,
  Divider,
  Grid,
  Group,
  Menu,
  rem,
  Stack,
  Text,
  Title,
  UnstyledButton
} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {
  Badge,
  LineByTimeInsight,
  ErrorPage,
  MonthPickerInput,
  ResizeableContent,
  StatisticsDataTable,
  ThemedPage,
  toStatisticsDataGroups,
  useApp,
  useAuth,
  useDownloadTicketsReport,
} from "@ots-frontend/ots-ui";
import {
  IconCalendar,
  IconChartDots3,
  IconChevronDown,
  IconCoins,
  IconFileInvoice,
  IconLayersDifference,
  IconTruck
} from "@tabler/icons-react";
import {useAppSelector} from "../../store/hooks/useAppSelector";
import {getAppCategory} from "../../store/ui/selectors";
import {useCallback, useMemo} from "react";
import {
  addMonths,
  adjustToTimeZone, costStatisticsTypes,
  EMPTY_STATISTICS_BUNDLE_BUILDER_OPTIONS, quantityStatisticsTypes,
  StatisticsSearchCriteria,
  statisticsSelector,
  statisticsType,
  transportMethod,
  useSearchStatisticsQuery
} from "@ots-frontend/ots-data";
import {getSettlementPageFilter} from "../../store/settlement-page/selectors";
import {useDispatch} from "react-redux";
import {setSettlementPageFilter} from "../../store/settlement-page/actions";
import {baseQueryApi} from "../../store/api";
import {useNavigate} from "react-router-dom";

export type FinancialPageProps = {}

export const FinancialPage = (props: FinancialPageProps) => {
  const {t} = useTranslation(["translation", "common"]);

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {regionId} = useApp()
  const {hasAccess} = useAuth()
  const appCategory = useAppSelector(getAppCategory);
  const filter = useAppSelector(getSettlementPageFilter)

  const criteria: StatisticsSearchCriteria = useMemo(() => ({
    fromDttm: adjustToTimeZone(filter.dttm).getTime(),
    toDttm: adjustToTimeZone(addMonths(filter.dttm, 1)).getTime(),
    regionIds: regionId ? [regionId] : undefined,
    categories: appCategory ? [appCategory.code] : undefined,
    includeSubTruckCompanies: true,
    includeTruckingData: true,
    includePipelineData: true,
  }), [filter, regionId, appCategory])

  const selectStatistics = useMemo(() => {
    return statisticsSelector()
  }, []);

  const {statisticsBundle, isLoading} = useSearchStatisticsQuery(criteria, {
    selectFromResult: (result) => ({
      statisticsBundle: selectStatistics(result, {
        ...EMPTY_STATISTICS_BUNDLE_BUILDER_OPTIONS,
        processRegion: true,
        processTruckCompany: true,
        processVendorCompany: true,
      }),
      isLoading: result.isLoading || result.isFetching,
    })
  });

  const {
    downloadStatisticsTicketsReport,
  } = useDownloadTicketsReport({baseQueryApi})

  const onExportClick = useCallback(() => {
    if (criteria) {
      downloadStatisticsTicketsReport(criteria)
    }
  }, [downloadStatisticsTicketsReport, criteria])

  const onFilterChange = useCallback((date: Date) => {
    console.log(date)
    dispatch(setSettlementPageFilter({
      dttm: date.getTime(),
    }));
  }, [dispatch]);


  if (!hasAccess("view_cost")) {
    return <ErrorPage/>
  }

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container w={size.width} h={size.height} className={classes.container} style={{"overflowY": "scroll"}}
                       fluid={true} p="md">
              <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                <Group position="apart">
                  <Group spacing="xs">
                    <IconCoins/>
                    <Title order={4}>
                      {t("page.financial.title", "Financial", {ns: "translation"})}
                    </Title>
                    <Badge color="blue">
                      Beta
                    </Badge>
                  </Group>
                  <Menu
                    width={260}
                    position="bottom-end"
                    transitionProps={{transition: "pop-top-right"}}
                    withinPortal
                  >
                    <Menu.Target>
                      <UnstyledButton>
                        <Group spacing={7}>
                          <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                            {t('common.action.plural', "Actions")}
                          </Text>
                          <IconChevronDown size={rem(12)} stroke={1.5}/>
                        </Group>
                      </UnstyledButton>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item onClick={() => onExportClick()}>
                        {t("common.action.export", "Export", {ns: "common"})}
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Group>

                <Group position="center" spacing="xs">
                  <IconCalendar size="1.1rem" stroke={1.5}/>
                  <MonthPickerInput
                    defaultValue={new Date(filter.dttm)}
                    onChange={onFilterChange}
                    popoverProps={{
                      withinPortal: true
                    }}/>
                </Group>
              </Card>
              <Grid>
                <Grid.Col span={12}>
                  <Group>
                    <Button
                      onClick={() => navigate("invoices")}
                      leftIcon={<IconFileInvoice/>}>
                      {t("common.entity.invoice.plural", "Invoices", {ns: "common"})}
                    </Button>
                    <Button
                      onClick={() => navigate("compare")}
                      leftIcon={<IconLayersDifference/>}>
                      {t("common.page.ticketsettlement", "Ticket Settlement", {ns: "common"})}
                    </Button>
                    <Button
                      onClick={() => navigate("/pipelines/settlement")}
                      leftIcon={<IconLayersDifference/>}>
                      {t("common.page.ticketsettlement", "Pipeline Settlement", {ns: "common"})}
                    </Button>
                  </Group>
                </Grid.Col>
                <Grid.Col span={12}>
                  <Divider my="sm"/>
                  <Card shadow="sm" padding="lg" mb="lg" radius="md">
                    <Stack>
                      <Title order={3}>
                        Trucking Settlement
                      </Title>
                      <StatisticsDataTable
                        withBorder
                        scrollAreaProps={{
                          offsetScrollbars: true
                        }}
                        method={transportMethod.trucking}
                        types={[
                          statisticsType.quantity,
                          statisticsType.netQuantity,
                          statisticsType.billableQuantity,
                          statisticsType.billableTime,
                          statisticsType.demurrageTimeCost,
                          statisticsType.ticketRejectLoadCost,
                          statisticsType.fuelSurchargeCost,
                          statisticsType.timeCost,
                          statisticsType.loadCost,
                          statisticsType.freightCost,
                          statisticsType.cost
                        ]}
                        category={appCategory}
                        records={statisticsBundle?.truckCompanyStats}
                        recordsPerPageOptions={[10]}
                        fetching={isLoading}
                        hideExpansion={true}
                        showTotals={true}/>
                    </Stack>
                  </Card>
                </Grid.Col>
                {criteria.fromDttm &&
                  <>
                    <Grid.Col span={6}>
                      <LineByTimeInsight
                        id="settlement_cost_by_time"
                        title={"Cost By Time"}
                        fromDttm={criteria.fromDttm}
                        toDttm={criteria.toDttm}
                        statisticsTypes={costStatisticsTypes}
                        data={statisticsBundle?.regionStats}
                        methods={transportMethod.trucking}
                        category={appCategory}/>
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <LineByTimeInsight
                        id="settlement_quantity_by_time"
                        title={"Quantity By Time"}
                        fromDttm={criteria.fromDttm}
                        toDttm={criteria.toDttm}
                        statisticsTypes={quantityStatisticsTypes}
                        data={statisticsBundle?.regionStats}
                        methods={transportMethod.trucking}
                        category={appCategory}/>
                    </Grid.Col>
                  </>
                }
                {statisticsBundle?.disposalStats?.map((stats, index) => {
                  return (
                    <Grid.Col span={12} key={index}>
                      <Divider my="sm"/>
                      <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                        <Stack>
                          <Title order={3}>
                            {stats.name + ": Disposal Settlement"}
                          </Title>
                          <Group mt="md" spacing="xs">
                            <IconTruck size={16}/>
                            <Text>Trucking</Text>
                          </Group>
                          <StatisticsDataTable
                            withBorder
                            withColumnBorders
                            scrollAreaProps={{
                              offsetScrollbars: true
                            }}
                            method={transportMethod.trucking}
                            types={[
                              statisticsType.quantity,
                              statisticsType.netQuantity,
                            ]}
                            category={appCategory}
                            records={Object.values(stats.truckCompanyMap)}
                            grouping={{
                              groupRetriever: (data) => data.siteMap,
                              groups: toStatisticsDataGroups(Object.values(stats.truckCompanyMap)?.map((subStats) => subStats.siteMap))
                            }}
                            recordsPerPageOptions={[10]}
                            fetching={isLoading}
                            hideExpansion={true}
                            showTotals={true}/>
                          <Group mt="md" spacing="xs">
                            <IconChartDots3 size={16}/>
                            <Text>Pipeline</Text>
                          </Group>
                          <StatisticsDataTable
                            withBorder
                            withColumnBorders
                            scrollAreaProps={{
                              offsetScrollbars: true
                            }}
                            method={transportMethod.pipeline}
                            types={[
                              statisticsType.quantity
                            ]}
                            category={appCategory}
                            records={Object.values(stats.pipelineMap)}
                            grouping={{
                              groupRetriever: (data) => data.siteMap,
                              groups: toStatisticsDataGroups(Object.values(stats.pipelineMap)?.map((subStats) => subStats.siteMap))
                            }}
                            recordsPerPageOptions={[10]}
                            fetching={isLoading}
                            hideExpansion={true}
                            showTotals={true}/>
                        </Stack>
                      </Card>
                    </Grid.Col>
                  )
                })}
              </Grid>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
